import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Select, Table } from 'flowbite-react';
import { Project, Release, Change } from '../../models';
import { useAppDispatch, useAppSelector, RootState } from '../../redux';
import { loadReleases, reorderReleaseChanges, loadChange, resetChanges, getChangelog, updateChange } from '../../redux';
import { Loader, ChangeModal, ChangelogUpcoming, Tooltip } from '../../components';
import { ChangeListHead, ChangeListRow } from '../ChangesList/ChangeListRow';
import { Icon } from '../Icon';

interface ProjectReleasesProps {
    project: Project;
}

function ProjectReleases(props: ProjectReleasesProps) {
    const { project } = props;

    const { t }    = useTranslation();
    const dispatch = useAppDispatch();

    const [releaseId, setReleaseId]         = useState<string | undefined>();
    const [orderedChanges, setOrderedChanges] = useState<Change[]>([]);
    const [currentChangeId, setCurrentChangeId] = useState<string | undefined>();

    const { user }   = useAppSelector((state: RootState) => state.auth);
    const { active } = useAppSelector((state: RootState) => state.tracks);
    const { change } = useAppSelector((state: RootState) => state.changes);

    const { loading, releases } = useAppSelector((state: RootState) => state.releases);
    const changelogState = useAppSelector((state: RootState) => state.projects.changelog);

    const projectsMap: Record<string, boolean> = {};
    const projectsIds = Object.keys(projectsMap);

    const selectChange = (c?: Change) => {
        setCurrentChangeId(c?._id);
        if (c)
            dispatch(loadChange(c._id));
        else
            dispatch(resetChanges(/* onlyItem */ true));
    };

    useEffect(() => {
        dispatch(loadReleases({ project: project._id }));
        dispatch(getChangelog(project._id));
    }, [dispatch, project._id]);

    const upcoming = changelogState?.project?._id === project._id ? (changelogState.upcoming || []) : [];
    const upcomingTypes = [
        Change.TYPE_FEATURE,
        Change.TYPE_FIX,
        Change.TYPE_UI,
        Change.TYPE_I18N,
        Change.TYPE_DOC,
        Change.TYPE_TEST,
        Change.TYPE_BUILD,
        Change.TYPE_REFACTOR,
        Change.TYPE_LINT,
        Change.TYPE_CHORE,
        Change.TYPE_DEPS,
    ];

    const ignoreChange = (c: Change, e?: React.MouseEvent) => {
        e?.stopPropagation();
        e?.preventDefault();
        dispatch(updateChange({ _id: c._id, omitFromChangelog: true }, true, true));
    };

    const onReleaseChange = (e: AnyObj) => {
        setReleaseId(e.target.value);
    }

    // select first release _id by default
    if (!releaseId && releases?.length > 0)
        setReleaseId(releases[0]._id);

    const release = releases?.find((r: Release) => r._id === releaseId);

    // Keep local ordered list in sync when the release changes.
    useEffect(() => {
        if (release?.changes)
            setOrderedChanges(release.changes);
    }, [releaseId, release?.changes]);

    const onDragEnd = (result: DropResult) => {
        if (!result.destination || !release) return;
        if (result.destination.index === result.source.index) return;

        const previous = orderedChanges;
        const reordered = Array.from(orderedChanges);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);

        setOrderedChanges(reordered);
        Promise.resolve(
            dispatch(reorderReleaseChanges(release._id, reordered.map((c: Change) => c._id)))
        ).then((json: AnyObj | void) => {
            if (!json?.release)
                setOrderedChanges(previous);
        }).catch(() => {
            setOrderedChanges(previous);
        });
    };

    return (
        <div className="ProjectReleases flex flex-col gap-2 mt-2">
            <ChangelogUpcoming
                upcoming={upcoming}
                orderedTypesToShow={upcomingTypes}
                onEdit={selectChange}
                onIgnore={ignoreChange}
            />
            <div className="flex justify-center items-center">
                <Select onChange={onReleaseChange} value={release?._id}>
                    <option>{t('changes.choose_a_release')}</option>
                    {releases?.map((rel: Release) => (
                        <option key={rel._id} value={rel._id}>
                            V. {rel.version}
                        </option>
                    ))}
                </Select>
            </div>
            <div className="flex">
                {(loading === 'pending') && (
                    <Loader />
                )}
                {orderedChanges.length > 0 && (loading !== 'pending') && (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Table striped hoverable className="w-full table-fixed border rounded">
                            <ChangeListHead
                                hideStatut
                                leadingColumn
                                projectsIds={projectsIds}
                            />
                            <Droppable droppableId="release-changes">
                                {(provided) => (
                                    <tbody
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800"
                                    >
                                        {orderedChanges.map((ch: Change, index: number) => (
                                            <Draggable key={ch._id} draggableId={ch._id} index={index}>
                                                {(dragProvided, snapshot) => (
                                                    <ChangeListRow
                                                        ref={dragProvided.innerRef}
                                                        rowProps={dragProvided.draggableProps}
                                                        rowClassName={[
                                                            'group',
                                                            snapshot.isDragging ? '!shadow-lg !bg-white dark:!bg-gray-700 relative z-10' : '',
                                                            ch.omitFromChangelog ? 'opacity-50' : '',
                                                        ].filter(Boolean).join(' ')}
                                                        change={ch}
                                                        hideStatut
                                                        projectsIds={projectsIds}
                                                        user={user}
                                                        active={active}
                                                        onSelectChange={selectChange}
                                                        leadingCell={(
                                                            <div className="flex items-center gap-0.5">
                                                                <div
                                                                    {...dragProvided.dragHandleProps}
                                                                    className="cursor-grab text-gray-400 hover:text-gray-600 dark:text-gray-300 px-1 py-2 -my-1"
                                                                    title={t('common.drag_to_reorder') || 'Drag to reorder'}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <Icon type="dots" size={4} />
                                                                </div>
                                                                {!ch.omitFromChangelog && (
                                                                    <Tooltip content={t('projects.ignore_from_changelog')}>
                                                                        <button
                                                                            type="button"
                                                                            title={t('projects.ignore_from_changelog') || ''}
                                                                            className="p-0.5 rounded text-gray-300 opacity-0 group-hover:opacity-100 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800"
                                                                            onClick={(e) => ignoreChange(ch, e)}
                                                                        >
                                                                            <Icon type="close" size={4} color="gray-400" />
                                                                        </button>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                        )}
                                                    />
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </Table>
                    </DragDropContext>
                )}
            </div>
            {change && change._id === currentChangeId && change.project?.name && (
                <ChangeModal
                    project={change.project}
                    change={change}
                    show={true}
                    showProjectName={true}
                    onClose={() => selectChange()}
                    onRemove={(values: AnyObj) => { }}
                />
            )}
        </div>
    );
}

export default ProjectReleases;
